<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="내 캠페인"
        subtitle="캠페인 링크 관리 및 통계"
      >
        <template #actions>
          <router-link
            to="/partners/ad/create"
            class="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            새 캠페인
          </router-link>
        </template>
      </PartnersHeader>
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <!-- 로딩 -->
        <div v-if="loading" class="bg-white border border-slate-200 rounded-2xl p-12 text-center">
        <div class="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p class="text-slate-500">캠페인을 불러오는 중...</p>
        </div>

        <!-- 빈 상태 -->
        <div v-else-if="myCampaigns.length === 0" class="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <div class="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-10 h-10 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-slate-600 mb-2">아직 캠페인이 없습니다</h3>
          <p class="text-slate-400 mb-6">나만의 전용 상담 링크를 만들어보세요.</p>
          <router-link
            to="/partners/ad/create"
            class="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            첫 캠페인 만들기
          </router-link>
        </div>

        <!-- 캠페인 목록 (3컬럼 그리드) -->
        <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 왼쪽: 바로가기 -->
          <div class="lg:col-span-1 space-y-4">
            <!-- 바로가기 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-4">
              <p class="text-[12px] font-bold text-slate-500 mb-3">바로가기</p>
              <div class="space-y-2">
                <router-link to="/partners/ad/create" class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                      </svg>
                    </div>
                    <span class="text-[13px] font-medium text-slate-700">새 캠페인 만들기</span>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
                <router-link to="/partners/point" class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span class="text-blue-600 text-[12px] font-bold">P</span>
                    </div>
                    <span class="text-[13px] font-medium text-slate-700">포인트 충전하기</span>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
                <router-link to="/partners/leads/customers" class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                    </div>
                    <span class="text-[13px] font-medium text-slate-700">내 고객 보관함</span>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
              </div>
            </div>

            <!-- 캠페인 통계 요약 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-5">
              <p class="text-[12px] font-bold text-slate-500 mb-3">전체 통계</p>
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <span class="text-[13px] text-slate-600">총 캠페인</span>
                  <span class="text-[15px] font-bold text-slate-800">{{ myCampaigns.length }}개</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-[13px] text-slate-600">활성 캠페인</span>
                  <span class="text-[15px] font-bold text-emerald-600">{{ myCampaigns.filter(c => c.status === 'active').length }}개</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-[13px] text-slate-600">총 조회수</span>
                  <span class="text-[15px] font-bold text-slate-800">{{ myCampaigns.reduce((sum, c) => sum + (c.views || 0), 0).toLocaleString() }}</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-[13px] text-slate-600">총 상담 신청</span>
                  <span class="text-[15px] font-bold text-blue-600">{{ myCampaigns.reduce((sum, c) => sum + (c.consultations || 0), 0).toLocaleString() }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 오른쪽: 캠페인 목록 -->
          <div class="lg:col-span-2 space-y-4">
            <div
              v-for="campaign in myCampaigns"
              :key="campaign.id"
              :class="[
                'bg-white border border-slate-200 rounded-2xl overflow-hidden',
                campaign.status === 'expired' ? 'opacity-60' : ''
              ]"
            >
              <!-- 상태 헤더 -->
              <div :class="[
                'px-5 py-3 flex items-center justify-between',
                campaign.status === 'active' ? 'bg-emerald-50' : 'bg-slate-100'
              ]">
                <div class="flex items-center gap-2">
                  <span :class="[
                    'w-2 h-2 rounded-full',
                    campaign.status === 'active' ? 'bg-emerald-500' : 'bg-slate-400'
                  ]"></span>
                  <span :class="[
                    'text-sm font-medium',
                    campaign.status === 'active' ? 'text-emerald-700' : 'text-slate-500'
                  ]">
                    {{ campaign.status === 'active' ? '활성' : '만료됨' }}
                  </span>
                </div>
                <span class="text-sm text-slate-500">
                  {{ campaign.status === 'active' ? `D-${getRemainingDays(campaign.endDate)}` : '종료' }}
                </span>
              </div>

              <!-- 캠페인 정보 -->
              <div class="p-5">
                <div class="flex items-start justify-between mb-4">
                  <div>
                    <h3 class="text-[15px] font-bold text-slate-800">{{ campaign.propertyName }}</h3>
                    <p class="text-[13px] text-slate-500">{{ campaign.durationLabel }} 캠페인</p>
                  </div>
                </div>

                <!-- 통계 -->
                <div class="grid grid-cols-3 gap-3 mb-4">
                  <div class="text-center p-3 bg-slate-50 rounded-xl">
                    <p class="text-xl font-bold text-emerald-600">{{ campaign.views || 0 }}</p>
                    <p class="text-[11px] text-slate-500">페이지 조회</p>
                  </div>
                  <div class="text-center p-3 bg-slate-50 rounded-xl">
                    <p class="text-xl font-bold text-blue-600">{{ campaign.consultations || 0 }}</p>
                    <p class="text-[11px] text-slate-500">상담 신청</p>
                  </div>
                  <div class="text-center p-3 bg-slate-50 rounded-xl">
                    <p class="text-xl font-bold text-violet-600">
                      {{ campaign.views > 0 ? ((campaign.consultations / campaign.views) * 100).toFixed(1) : 0 }}%
                    </p>
                    <p class="text-[11px] text-slate-500">전환율</p>
                  </div>
                </div>

                <!-- 기간 정보 -->
                <div class="flex items-center gap-4 text-[13px] text-slate-500 mb-4">
                  <span>{{ campaign.startDate }} ~ {{ campaign.endDate }}</span>
                  <span>|</span>
                  <span>{{ campaign.price.toLocaleString() }}P</span>
                </div>

                <!-- 링크 -->
                <div class="flex items-center gap-2 mb-4">
                  <input
                    type="text"
                    :value="getFullUrl(campaign.code)"
                    readonly
                    class="flex-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                  />
                  <button
                    @click="copyLink(campaign.code)"
                    class="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                    :disabled="campaign.status === 'expired'"
                  >
                    복사
                  </button>
                  <a
                    :href="getFullUrl(campaign.code)"
                    target="_blank"
                    class="px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50"
                  >
                    열기
                  </a>
                </div>

                <!-- 액션 버튼 (활성 캠페인만) -->
                <div v-if="campaign.status === 'active'" class="flex items-center gap-2 pt-4 border-t border-slate-100">
                  <button
                    @click="openExtendModal(campaign)"
                    class="flex-1 flex items-center justify-center gap-2 py-2.5 bg-violet-50 text-violet-700 text-sm font-medium rounded-lg hover:bg-violet-100 transition"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    기간 연장
                  </button>
                  <button
                    @click="handleDelete(campaign)"
                    class="flex items-center justify-center gap-2 px-4 py-2.5 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                    삭제
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 삭제 확인 모달 -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div class="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div class="p-6">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <div>
              <h3 class="text-xl font-bold text-slate-800">캠페인 삭제</h3>
              <p class="text-[13px] text-slate-500">정말 삭제하시겠습니까?</p>
            </div>
          </div>

          <!-- 삭제될 캠페인 정보 -->
          <div class="bg-slate-50 rounded-xl p-4 mb-4">
            <p class="text-[13px] text-slate-500 mb-1">삭제될 캠페인</p>
            <p class="font-semibold text-slate-800">{{ deletingCampaign?.propertyName }}</p>
            <p class="text-[12px] text-slate-400">{{ deletingCampaign?.durationLabel }} | {{ deletingCampaign?.startDate }} ~ {{ deletingCampaign?.endDate }}</p>
          </div>

          <!-- 로딩 상태 -->
          <div v-if="loadingAds" class="flex items-center justify-center py-6">
            <div class="w-8 h-8 border-4 border-slate-200 border-t-red-500 rounded-full animate-spin"></div>
          </div>

          <!-- 연결된 광고 목록 -->
          <div v-else-if="totalLinkedAdsCount > 0" class="mb-4">
            <div class="bg-red-50 border border-red-200 rounded-xl p-4">
              <div class="flex items-center gap-2 mb-3">
                <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <span class="text-[14px] font-bold text-red-700">함께 삭제되는 광고</span>
              </div>

              <div class="space-y-2">
                <!-- 인앱 광고 -->
                <div v-if="linkedAds.adBanners.length > 0" class="flex items-center justify-between py-2 border-b border-red-100 last:border-0">
                  <div class="flex items-center gap-2">
                    <span class="px-2 py-0.5 bg-violet-100 text-violet-700 text-[11px] font-bold rounded">IN-APP</span>
                    <span class="text-[13px] text-slate-700">인앱 광고</span>
                  </div>
                  <span class="text-[13px] font-bold text-red-600">{{ linkedAds.adBanners.length }}개</span>
                </div>

                <!-- 팝업 광고 -->
                <div v-if="linkedAds.popupAds.length > 0" class="flex items-center justify-between py-2 border-b border-red-100 last:border-0">
                  <div class="flex items-center gap-2">
                    <span class="px-2 py-0.5 bg-rose-100 text-rose-700 text-[11px] font-bold rounded">POPUP</span>
                    <span class="text-[13px] text-slate-700">팝업 광고</span>
                  </div>
                  <span class="text-[13px] font-bold text-red-600">{{ linkedAds.popupAds.length }}개</span>
                </div>

                <!-- 스플래시 광고 -->
                <div v-if="linkedAds.splashAds.length > 0" class="flex items-center justify-between py-2 border-b border-red-100 last:border-0">
                  <div class="flex items-center gap-2">
                    <span class="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-[11px] font-bold rounded">SPLASH</span>
                    <span class="text-[13px] text-slate-700">스플래시 광고</span>
                  </div>
                  <span class="text-[13px] font-bold text-red-600">{{ linkedAds.splashAds.length }}개</span>
                </div>
              </div>

              <p class="text-[12px] text-red-600 mt-3">
                위 광고들은 캠페인과 함께 영구 삭제되며 복구할 수 없습니다.
              </p>
            </div>
          </div>

          <!-- 연결된 광고 없음 -->
          <div v-else class="mb-4">
            <div class="bg-slate-50 rounded-xl p-4 text-center">
              <p class="text-[13px] text-slate-500">연결된 광고가 없습니다.</p>
            </div>
          </div>

          <p class="text-[13px] text-slate-500 text-center">
            삭제 시 환불되지 않으며, 캠페인 링크가 더 이상 작동하지 않습니다.
          </p>
        </div>

        <div class="px-6 pb-6 flex gap-3">
          <button
            @click="closeDeleteModal"
            class="flex-1 py-3 border border-slate-300 text-slate-600 font-medium rounded-xl hover:bg-slate-50"
          >
            취소
          </button>
          <button
            @click="confirmDelete"
            :disabled="deleting || loadingAds"
            class="flex-1 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ deleting ? '삭제 중...' : '삭제하기' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 기간 연장 모달 -->
    <div v-if="showExtendModal" class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div class="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div class="p-6">
          <h3 class="text-xl font-bold text-slate-800 mb-2">기간 연장</h3>
          <p class="text-[13px] text-slate-500 mb-4">캠페인 기간을 연장합니다. 연장 기간은 현재 종료일 이후부터 적용됩니다.</p>

          <!-- 현재 캠페인 정보 -->
          <div class="bg-slate-50 rounded-xl p-4 mb-4">
            <p class="text-[13px] text-slate-500 mb-1">현재 종료일</p>
            <p class="font-semibold text-slate-800">{{ selectedCampaign?.endDate }}</p>
          </div>

          <!-- 연장 옵션 선택 -->
          <div class="space-y-2 mb-4">
            <button
              v-for="(option, key) in extensionOptions"
              :key="key"
              @click="selectedExtension = key"
              :class="[
                'w-full p-4 rounded-xl border-2 transition text-left flex items-center justify-between',
                selectedExtension === key
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-slate-200 hover:border-violet-300'
              ]"
            >
              <div>
                <p class="font-semibold text-slate-800">{{ option.label }} 연장</p>
                <p class="text-[12px] text-slate-500">+{{ option.days }}일</p>
              </div>
              <div class="text-right">
                <span v-if="option.originalPrice" class="text-sm text-slate-400 line-through mr-2">{{ option.originalPrice.toLocaleString() }}</span>
                <span class="text-lg font-bold text-violet-600">{{ option.price.toLocaleString() }}</span>
                <span v-if="option.discount" class="ml-1 text-xs px-1.5 py-0.5 bg-rose-100 text-rose-600 rounded">{{ option.discount }}%</span>
              </div>
            </button>
          </div>

          <!-- 연장 후 종료일 -->
          <div v-if="selectedExtension" class="bg-violet-50 rounded-xl p-4 mb-4">
            <p class="text-[13px] text-violet-600 mb-1">연장 후 종료일</p>
            <p class="font-bold text-violet-800">{{ getNewEndDate() }}</p>
          </div>

          <!-- 포인트 잔액 부족 경고 -->
          <div v-if="selectedExtension && extensionOptions[selectedExtension].price > coinBalance" class="bg-red-50 rounded-xl p-3 mb-4">
            <p class="text-sm text-red-600">포인트가 부족합니다. 충전 후 다시 시도해주세요.</p>
          </div>
        </div>

        <div class="px-6 pb-6 flex gap-3">
          <button
            @click="closeExtendModal"
            class="flex-1 py-3 border border-slate-300 text-slate-600 font-medium rounded-xl hover:bg-slate-50"
          >
            취소
          </button>
          <button
            @click="handleExtend"
            :disabled="!selectedExtension || extending || (selectedExtension && extensionOptions[selectedExtension].price > coinBalance)"
            class="flex-1 py-3 bg-violet-600 text-white font-medium rounded-xl hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ extending ? '연장 중...' : '연장하기' }}
          </button>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'
import { useCampaign } from '@/composables/partners/useCampaign'
import { usePoint } from '@/composables/partners/usePoint'

export default {
  name: 'MyCampaignsView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const {
      myCampaigns,
      loading,
      loadMyCampaigns,
      getRemainingDays,
      getExtensionPricing,
      getLinkedAds,
      deleteCampaign,
      extendCampaign
    } = useCampaign()
    const { balance: coinBalance, loadBalance } = usePoint()

    // 연장 모달 관련
    const showExtendModal = ref(false)
    const selectedCampaign = ref(null)
    const selectedExtension = ref(null)
    const extending = ref(false)

    // 삭제 확인 모달 관련
    const showDeleteModal = ref(false)
    const deletingCampaign = ref(null)
    const linkedAds = ref({ adBanners: [], popupAds: [], splashAds: [] })
    const deleting = ref(false)
    const loadingAds = ref(false)

    const extensionOptions = computed(() => getExtensionPricing())

    const getFullUrl = (code) => {
      return `${window.location.origin}/p/${code}`
    }

    const copyLink = async (code) => {
      try {
        await navigator.clipboard.writeText(getFullUrl(code))
        alert('링크가 복사되었습니다!')
      } catch {
        alert('복사에 실패했습니다.')
      }
    }

    // 삭제 확인 모달 열기
    const handleDelete = async (campaign) => {
      deletingCampaign.value = campaign
      loadingAds.value = true
      showDeleteModal.value = true

      // 연결된 광고 조회
      linkedAds.value = await getLinkedAds(campaign.id)
      loadingAds.value = false
    }

    // 삭제 확인 모달 닫기
    const closeDeleteModal = () => {
      showDeleteModal.value = false
      deletingCampaign.value = null
      linkedAds.value = { adBanners: [], popupAds: [], splashAds: [] }
    }

    // 삭제 확정
    const confirmDelete = async () => {
      if (!deletingCampaign.value || deleting.value) return

      deleting.value = true

      const result = await deleteCampaign(deletingCampaign.value.id, true)

      deleting.value = false

      if (result.success) {
        alert('캠페인과 연결된 광고가 모두 삭제되었습니다.')
        closeDeleteModal()
      } else {
        alert('삭제 실패: ' + result.error)
      }
    }

    // 총 삭제 광고 수
    const totalLinkedAdsCount = computed(() => {
      return linkedAds.value.adBanners.length +
             linkedAds.value.popupAds.length +
             linkedAds.value.splashAds.length
    })

    // 연장 모달 열기
    const openExtendModal = (campaign) => {
      selectedCampaign.value = campaign
      selectedExtension.value = null
      showExtendModal.value = true
    }

    // 연장 모달 닫기
    const closeExtendModal = () => {
      showExtendModal.value = false
      selectedCampaign.value = null
      selectedExtension.value = null
    }

    // 새 종료일 계산
    const getNewEndDate = () => {
      if (!selectedCampaign.value || !selectedExtension.value) return ''

      const option = extensionOptions.value[selectedExtension.value]
      const currentEnd = new Date(selectedCampaign.value.endDate)
      currentEnd.setDate(currentEnd.getDate() + option.days)

      const y = currentEnd.getFullYear()
      const m = String(currentEnd.getMonth() + 1).padStart(2, '0')
      const d = String(currentEnd.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }

    // 연장 처리
    const handleExtend = async () => {
      if (!selectedCampaign.value || !selectedExtension.value || extending.value) return

      if (!confirm('캠페인 기간을 연장하시겠습니까?')) return

      extending.value = true

      const result = await extendCampaign(selectedCampaign.value.id, selectedExtension.value)

      extending.value = false

      if (result.success) {
        alert(`캠페인이 ${result.daysAdded}일 연장되었습니다.\n새 종료일: ${result.newEndDate}`)
        await loadBalance()
        closeExtendModal()
      } else {
        alert('연장 실패: ' + result.error)
      }
    }

    onMounted(async () => {
      await loadMyCampaigns()
      await loadBalance()
    })

    return {
      myCampaigns,
      loading,
      coinBalance,
      getRemainingDays,
      getFullUrl,
      copyLink,
      handleDelete,
      showExtendModal,
      selectedCampaign,
      selectedExtension,
      extensionOptions,
      extending,
      openExtendModal,
      closeExtendModal,
      getNewEndDate,
      handleExtend,
      // 삭제 모달
      showDeleteModal,
      deletingCampaign,
      linkedAds,
      deleting,
      loadingAds,
      totalLinkedAdsCount,
      closeDeleteModal,
      confirmDelete
    }
  }
}
</script>
